// 관리자 대시보드 전용 API (PDF 4.1 / 3.2)
// - permission: 사용자별 시험성적서 발행 권한 CRUD
// - revenue   : 매출액 집계 / CSV export
// - sampleCount: 시료수 입고/완료/잔량 요약
// - account   : 계정 관리 CRUD (UI 후속)
class Admin {
	constructor(instance) {
		this.axios = instance
	}

	// ── permission ────────────────────────────────────────────────
	async getPermissionList(params) {
		const response = await this.axios.get('/admin/permission/list', { params })
		return response.data
	}
	async getPermissionByUser(userId) {
		const response = await this.axios.get(`/admin/permission/${userId}`)
		return response.data
	}
	async createPermission(body) {
		const response = await this.axios.post('/admin/permission', body)
		return response.data
	}
	async updatePermission(id, body) {
		const response = await this.axios.put(`/admin/permission/${id}`, body)
		return response.data
	}
	async deletePermission(id) {
		const response = await this.axios.delete(`/admin/permission/${id}`)
		return response.data
	}
	async getPermissionUserList(params) {
		const response = await this.axios.get('/admin/permission/user-list', { params })
		return response.data
	}
	async getPermissionReportForms(params) {
		const response = await this.axios.get('/admin/permission/report-forms', { params })
		return response.data
	}
	async getPermissionEditor(userId) {
		const response = await this.axios.get(`/admin/permission/editor/${userId}`)
		return response.data
	}
	async replacePermissionBulk(userId, body) {
		const response = await this.axios.put(`/admin/permission/bulk/${userId}`, body)
		return response.data
	}

	// ── revenue ───────────────────────────────────────────────────
	async getRevenueSummary(params) {
		const response = await this.axios.get('/admin/revenue/summary', { params })
		return response.data
	}
	getRevenueExportUrl(params) {
		// 레거시/디버그용. 새 탭에서는 Authorization 이 없어 401이 나므로
		// 실제 다운로드는 exportRevenueCsv(blob) 사용.
		const search = new URLSearchParams()
		Object.entries(params || {}).forEach(([k, v]) => {
			if (v !== undefined && v !== null && v !== '') search.append(k, v)
		})
		const qs = search.toString()
		const base = this.axios.defaults.baseURL || ''
		return `${base}/admin/revenue/export${qs ? `?${qs}` : ''}`
	}

	/** 인증 헤더가 포함된 요청으로 CSV 바이너리 수신 (새 탭 링크 대체용). */
	async exportRevenueCsv(params) {
		return this.axios.get('/admin/revenue/export', {
			params,
			responseType: 'blob'
		})
	}

	// ── sample-count ──────────────────────────────────────────────
	async getSampleCountSummary(params) {
		const response = await this.axios.get('/admin/sample-count/summary', { params })
		return response.data
	}

	async getSampleCountTestItems(params) {
		const response = await this.axios.get('/admin/sample-count/test-items', { params })
		return response.data
	}

	async exportSampleCountCsv(params) {
		return this.axios.get('/admin/sample-count/export', {
			params,
			responseType: 'blob'
		})
	}

	// ── account (UI 후속) ─────────────────────────────────────────
	async getAccountList(params) {
		const response = await this.axios.get('/admin/account/list', { params })
		return response.data
	}
	async getAccountDeptOptions() {
		const response = await this.axios.get('/admin/account/meta/departments')
		return response.data
	}
	async getAccount(id) {
		const response = await this.axios.get(`/admin/account/${id}`)
		return response.data
	}
	async updateAccountSignature(id, file) {
		const data = new FormData()
		data.append('file', file)
		const response = await this.axios.put(`/admin/account/${id}/signature`, data, {
			headers: { 'Content-Type': 'multipart/form-data' }
		})
		return response.data
	}
	async createAccount(body) {
		const response = await this.axios.post('/admin/account', body)
		return response.data
	}
	async updateAccount(id, body) {
		const response = await this.axios.put(`/admin/account/${id}`, body)
		return response.data
	}
	async resetAccountPassword(id, newPassword) {
		const response = await this.axios.patch(`/admin/account/${id}/password`, { newPassword })
		return response.data
	}
	/** @param {number|string} id
	 *  @param {{ deleteDatetime?: string|null }} [options] 퇴사일(YYYY-MM-DD 등, 선택). 미입력 시 서버 현재 시각 */
	async deleteAccount(id, options = {}) {
		const params = {}
		if (options.deleteDatetime) params.deleteDatetime = options.deleteDatetime
		const response = await this.axios.delete(`/admin/account/${id}`, { params })
		return response.data
	}
	/** 퇴사(소프트 삭제) 계정을 재직으로 복구 */
	async restoreAccount(id) {
		const response = await this.axios.post(`/admin/account/${id}/restore`)
		return response.data
	}

	// ── permission matrix (역할 x 기능) ───────────────────────────
	async getPermissionMatrix() {
		const response = await this.axios.get('/admin/matrix')
		return response.data
	}
	/** changes: [{ role, featureKey, enabled }] */
	async updatePermissionMatrix(changes) {
		const response = await this.axios.put('/admin/matrix', { changes })
		return response.data
	}

	// ── department (부서 관리 CRUD) ────────────────────────────────
	/** 부서 목록 조회 (kind: 'DUTY' | 'TEST' | undefined=전체) */
	async getDepartmentList(kind) {
		const params = kind ? { kind } : {}
		const response = await this.axios.get('/admin/department', { params })
		return response.data
	}
	async createDepartment(body) {
		const response = await this.axios.post('/admin/department', body)
		return response.data
	}
	/** cascade: true → 기존 user.dept_id / project.analysis_dept_id 일괄 갱신 */
	async updateDepartment(id, body) {
		const response = await this.axios.put(`/admin/department/${id}`, body)
		return response.data
	}
	async deleteDepartment(id) {
		const response = await this.axios.delete(`/admin/department/${id}`)
		return response.data
	}

	// ── project-type-code (업무구분 코드 관리) ─────────────────────
	async getProjectTypeCodeList() {
		const response = await this.axios.get('/admin/project-type-code')
		return response.data
	}
	/** body: { code, name, sortOrder? } */
	async createProjectTypeCode(body) {
		const response = await this.axios.post('/admin/project-type-code', body)
		return response.data
	}
	/** 이름만 수정 (코드는 바꿀 수 없다). 기존 업무의 «구분» 표기도 서버가 함께 갱신한다 */
	async updateProjectTypeCode(code, body) {
		const response = await this.axios.patch(
			`/admin/project-type-code/${encodeURIComponent(code)}`,
			body
		)
		return response.data
	}
	async deleteProjectTypeCode(code) {
		const response = await this.axios.delete(`/admin/project-type-code/${encodeURIComponent(code)}`)
		return response.data
	}

	// ── project-import ─────────────────────────────────────────────
	/** 접수대장 엑셀 일괄 임포트 / 누락 필드 보완
	 * @param {File} file  xlsx 파일 객체
	 * @param {boolean} dryRun  true: 미리보기만, false: 실제 DB 반영
	 * @param {'insert'|'patch'} mode  insert: 신규 등록, patch: 기존 레코드 누락 필드 보완
	 * @param {{offset?: number, limit?: number}} [chunk]
	 *   순차(청크) 처리 범위 — 유효 행 순번 기준. 생략하면 서버가 파일 전체를 한 번에 처리한다.
	 *   수천 행짜리 접수대장은 한 요청에 다 돌리면 프록시 타임아웃(504)에 걸리므로
	 *   화면은 같은 파일을 offset 만 바꿔 순차 호출하고 응답의 chunk.hasMore 로 이어간다.
	 * @param {{category?: string, amountMode?: string, createMissingClients?: boolean, overwriteCategory?: boolean}} [options]
	 *   category   : SOIL | WATER | AIR | DISPOSAL — 파일에 담당부서 열이 없으면 필수 (2026-09-20)
	 *   amountMode : ex(VAT별도×1.1, 기본) | inc(별도 열을 포함가로 간주) | publish(1회차 발행금액 우선). VAT포함 열이 있으면 무시
	 *   createMissingClients : insert 실행 시 DB 에 없는 고객사를 검수 없이 자동 등록
	 *   overwriteCategory    : patch 모드에서 category 로 기존 분야를 덮어쓴다
	 */
	async uploadProjectImport(file, dryRun = true, mode = 'insert', chunk = {}, options = {}) {
		const fd = new FormData()
		fd.append('file', file)
		fd.append('dryRun', String(dryRun))
		fd.append('mode', mode)
		if (chunk.offset != null) fd.append('offset', String(chunk.offset))
		if (chunk.limit != null) fd.append('limit', String(chunk.limit))
		if (options.category) fd.append('category', options.category)
		if (options.amountMode) fd.append('amountMode', options.amountMode)
		if (options.createMissingClients) fd.append('createMissingClients', 'true')
		if (options.overwriteCategory) fd.append('overwriteCategory', 'true')
		if (options.fillPublishTransactDate) fd.append('fillPublishTransactDate', 'true')
		if (options.overwriteAmount) fd.append('overwriteAmount', 'true')
		const response = await this.axios.post('/admin/project-import', fd, {
			headers: { 'Content-Type': 'multipart/form-data' },
			timeout: 5 * 60 * 1000, // 5분
		})
		return response.data
	}

	/** 접수대장의 고객사 후보 추출(dryRun=true) / 검수된 후보 등록(dryRun=false)
	 * @param {File} file  xlsx 파일 (후보 추출은 파일 전체 기준, 등록 시에는 원문 표기 별칭 기록용)
	 * @param {boolean} dryRun
	 * @param {Array} [selected]  등록할 후보 [{ key, name, aliases, bizNum, ceoName, phone, fax, address, managerName, managerContact, managerEmail, memo }]
	 */
	async uploadProjectImportClients(file, dryRun = true, selected = []) {
		const fd = new FormData()
		if (file) fd.append('file', file)
		fd.append('dryRun', String(dryRun))
		if (!dryRun) fd.append('selected', JSON.stringify(selected))
		const response = await this.axios.post('/admin/project-import/clients', fd, {
			headers: { 'Content-Type': 'multipart/form-data' },
			timeout: 5 * 60 * 1000,
		})
		return response.data
	}
}

export default Admin
